import React, { useState, useEffect, useMemo, useRef } from "react";
import { Controller, Control, useWatch } from "react-hook-form";
import Select, { StylesConfig } from "react-select";
import { Tooltip } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import useDebounce from "../../../hooks/useDebounce";
import usePrefetchProducts from "../../../hooks/usePrefetchProducts";
import usePrefetchItems from "../../../hooks/usePrefetchItems";
import useUserProfile from "../../../hooks/useUserProfile";
import { useTranslation } from "react-i18next";
import SelectNoOptionsMessage from "./SelectNoOptionsMessage";
import useConfigFieldsValues from "@/hooks/useConfigFieldsValues";
import { useUseCases } from "@/context/UseCasesProvider";
import { CgUnavailable } from "react-icons/cg";
import { HiOutlineBadgeCheck } from "react-icons/hi";
import { BsStars } from "react-icons/bs";

interface SelectComponentProps {
  apiUrl: string;
  control: Control<any>;
  endpoint: string;
  name: string;
  defaultValue: string | null;
  customerId?: string;
  productId?: string;
  valueConfidence?: number;
  isModified?: boolean;
  onSelectionChange?: (selectedOption: OptionType | null) => void;
  variantsAvailable?: (available: boolean) => void;
  classNames?: Record<string, string>;
  isRequired?: boolean;
  fieldConfig?: any;
  customTabIndex?: number;
  proposals?: string[];
  entityId?: string;
}

interface OptionType {
  value: string;
  label: string;
  customer_email?: string;
  customer_id?: string;
  delivery_address?: string;
  billing_address?: string;
  sender_postal_address?: string;
  product_available?: boolean;
  product_label?: string;
  customer_available?: boolean;
  customer_availability_description?: string;
}

interface SettingsInterface {
  settings: any;
}

type OptionsType = OptionType[];

const SelectComponent: React.FC<SelectComponentProps> = ({
  apiUrl,
  control,
  endpoint,
  name,
  defaultValue,
  customerId,
  productId,
  valueConfidence,
  isModified,
  onSelectionChange,
  variantsAvailable = () => {},
  fieldConfig,
  customTabIndex,
  proposals,
  entityId,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>(""); // State for search
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isDataReadOnlyOperators } = useUserProfile();
  const [hasTyped, setHasTyped] = useState(false);
  const { checksOrderMetadata } = useConfigFieldsValues();
  const settingsInterface = useUseCases() as SettingsInterface;

  const { data: pastPurchasedProductIds } = useQuery<Set<string>>({
    queryKey: ["pastPurchases", customerId],
    enabled: false,
  });

  const { addProducts } = usePrefetchProducts("products");

  const { addItems: addItemsCustomers } = usePrefetchItems("customers", "customer");

  const noOptionsMessage = useMemo(
    () => <SelectNoOptionsMessage isLoading={isLoading} t={t} />,
    [isLoading, t]
  );

  const displayNameInCaps = settingsInterface.settings.display.product_select.product_id_caps;

  useEffect(() => {
    if (debouncedSearchTerm) {
      if (endpoint === "products") {
        // Call addProducts with debounced input
        addProducts(debouncedSearchTerm, customerId ?? "", {
          onFetchStart: () => setIsLoading(true),
          onFetchEnd: () => setIsLoading(false),
          entityId,
        });
      } else if (endpoint === "customers" || (checksOrderMetadata?.sender_address_matching?.onlyInDeliveryPerCustomer === false)) {
        addItemsCustomers(debouncedSearchTerm, {
          onFetchStart: () => setIsLoading(true),
          onFetchEnd: () => setIsLoading(false),
        });
      }
    }
  }, [debouncedSearchTerm, addProducts, addItemsCustomers, customerId, endpoint]);

  // Fetch proposal details when proposals are provided
  const proposalsFetchedRef = useRef<string>("");
  useEffect(() => {
    if (endpoint === "products" && proposals && proposals.length > 0) {
      const proposalsKey = proposals.join(",");
      if (proposalsFetchedRef.current !== proposalsKey) {
        proposalsFetchedRef.current = proposalsKey;
        addProducts(proposals, customerId ?? "");
      }
    }
  }, [proposals, endpoint, addProducts, customerId]);

  const onSearchChange = (searchValue: string) => {
    if (!hasTyped && searchValue.trim() !== "") {
      setHasTyped(true);
    }
    setSearchTerm(searchValue); // Update searchTerm on each entry
  };

  const { data: productsData } = useQuery<OptionsType | undefined>({
    queryKey: ["allProducts"],
    enabled: false,
  });

  const { data: customersData } = useQuery<OptionsType | undefined>({
    queryKey: ["listCustomer"],
    enabled: false,
  });

  const customerDeliveryAddresses =
    customersData?.find((customer) => customer.customer_id === customerId)?.delivery_address ?? [];
  const customerBillingAddresses =
    customersData?.find((customer) => customer.customer_id === customerId)?.billing_address ?? [];
  const customerSenderPostalAddress =
    customersData?.find((customer) => customer.customer_id === customerId)?.sender_postal_address ?? [];

    const getAddressOptions = (addressType, metadataConfig, addressData) => {
      const useCustomerData = metadataConfig?.onlyInDeliveryPerCustomer === false;
      if (useCustomerData) {
        return customersData;
      }
      
      return addressData.length > 0 ? (addressData as any[]) : [];
    };
    
    const optionsData = useMemo(() => {
      if (endpoint === "products" || endpoint === "variants") {
        return productsData;
      }
    
      if (endpoint === "delivery_address") {
        return getAddressOptions(
          "delivery",
          checksOrderMetadata?.delivery_address_matching,
          customerDeliveryAddresses
        );
      }
    
      if (endpoint === "billing_address") {
        return getAddressOptions(
          "billing",
          checksOrderMetadata?.billing_address_matching,
          customerBillingAddresses
        );
      }
    
      if (endpoint === "sender_postal_address") {
        return getAddressOptions(
          "sender",
          checksOrderMetadata?.sender_address_matching,
          customerSenderPostalAddress
        );
      }
    
      return customersData;
    }, [
      endpoint, 
      productsData, 
      customersData, 
      checksOrderMetadata, 
      customerDeliveryAddresses, 
      customerBillingAddresses, 
      customerSenderPostalAddress
    ]);

  // Function to sort options
  const sortOptions = (options: OptionsType, searchTerm: string) => {
    return options.sort((a, b) => {
      const labelA = a.label.toLowerCase();
      const labelB = b.label.toLowerCase();
      const term = searchTerm.toLowerCase();

      // Priority 1: Past purchases first (only for products endpoint)
      if (endpoint === "products" && pastPurchasedProductIds) {
        const aIsPastPurchase = pastPurchasedProductIds.has(String(a.value));
        const bIsPastPurchase = pastPurchasedProductIds.has(String(b.value));
        if (aIsPastPurchase && !bIsPastPurchase) return -1;
        if (!aIsPastPurchase && bIsPastPurchase) return 1;
      }

      // Priority 2: Search term match
      if (labelA.startsWith(term) && !labelB.startsWith(term)) {
        return -1;
      }
      if (!labelA.startsWith(term) && labelB.startsWith(term)) {
        return 1;
      }
      return 0;
    });
  };

  // Map data to the format expected by Select
  const mappedOptions: OptionsType = useMemo(() => {
    if (!optionsData) {
      return [];
    }
    let mapped: OptionsType = [];
    if (endpoint === "products") {
      // Group products by product_id
      const productGroups = optionsData.reduce((acc: any, item: any) => {
        if (!acc[item.product_id]) {
          acc[item.product_id] = [];
        }
        acc[item.product_id].push(item);
        return acc;
      }, {});

      // Map each group to a single option
      mapped = Object.values(productGroups).map((group: any) => ({
        value: group[0].product_id,
        label: `${group[0].product_id} - ${
          group[0].product_atom_moq || group[0].product_moq
            ? " [" + group[0].product_atom_moq + "/" + group[0].product_moq + "] "
            : ""
        } ${group[0].product_name} ${
          group[0].product_secondary_id ? " - " + group[0].product_secondary_id : ""
        }`,
        product_available: group[0].product_available,
        product_label: group[0].product_label,
      }));
    } else if (endpoint === "customers") {
      mapped = optionsData?.map((item: any) => ({
        value: item.customer_id,
        label: `${item.customer_id} - ${item.customer_name} - (${
          item.customer_address
        } ${item.customer_postalCode} ${item.customer_city}) ${
          item.customer_secondary_id ? " - " + item.customer_secondary_id : ""
        }`,
        customer_email: item.customer_email,
        searchItem: item.searchItem,
        customer_available: item.customer_available,
        customer_availability_description: item.customer_availability_description,
      }));
    } else if (endpoint === "variants") {
      mapped = optionsData
        ?.filter((item: any) => item.product_id === productId && item.product_variant_name)
        .map((item: any) => ({
          value: item.product_variant_id,
          label: `${item.product_variant_name}`,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    } else if (
      endpoint === "delivery_address" ||
      endpoint === "billing_address" ||
      endpoint === "sender_postal_address"
    ) {
      if (!optionsData.length) {
        return [];
      }

      const useCustomerSpecificAddresses =
        (endpoint === "delivery_address" &&
          checksOrderMetadata?.delivery_address_matching?.onlyInDeliveryPerCustomer !== false) ||
        (endpoint === "billing_address" &&
          checksOrderMetadata?.billing_address_matching?.onlyInDeliveryPerCustomer !== false) ||
        (endpoint === "sender_postal_address" &&
          checksOrderMetadata?.sender_address_matching?.onlyInDeliveryPerCustomer !== false);

      if (useCustomerSpecificAddresses) {
        mapped = optionsData?.map((item: any) => ({
          value: item.address_id,
          label: `${item.address_id} - ${item.name} - ${item.street_number} ${item.address} ${item.postal_code} ${item.city} ${item.complement}${item.default ? " (défaut)" : ""}`,
          valueDetail: {
            name: item.name,
            street_number: item.street_number,
            address: item.address,
            city: item.city,
            postal_code: item.postal_code,
            country: item.country,
            complement: item.complement,
            contact: item.contact
          },
        }));
      } else {
        mapped = optionsData?.map((item: any) => ({
          value: item.customer_id,
          label: `${item.customer_id} - ${item.customer_name} - (${
            item.customer_address
          } ${item.customer_postalCode} ${item.customer_city}) ${
            item.customer_secondary_id ? " - " + item.customer_secondary_id : ""
          }`,
          valueDetail: {
            name: item.customer_name,
            //street_number: item.street_number,
            address: item.customer_address,
            city: item.customer_city,
            postal_code: item.customer_postalCode,
            //country: item.country,
            //complement: item.complement,
            //contact: item.contact
          },
        }));
      }
    }
    return sortOptions(mapped, debouncedSearchTerm);
  }, [optionsData, endpoint, productId, debouncedSearchTerm]);

  // Map proposals to OptionType format from cached productsData
  const proposalSet = useMemo(() => new Set(proposals || []), [proposals]);
  const proposalMappedOptions: OptionsType = useMemo(() => {
    if (endpoint !== "products" || !productsData || proposalSet.size === 0) {
      return [];
    }
    const productGroups = productsData.reduce((acc: any, item: any) => {
      if (proposalSet.has(item.product_id)) {
        if (!acc[item.product_id]) {
          acc[item.product_id] = [];
        }
        acc[item.product_id].push(item);
      }
      return acc;
    }, {});

    return Object.values(productGroups).map((group: any) => ({
      value: group[0].product_id,
      label: `${group[0].product_id} - ${
        group[0].product_atom_moq || group[0].product_moq
          ? " [" + group[0].product_atom_moq + "/" + group[0].product_moq + "] "
          : ""
      } ${group[0].product_name} ${
        group[0].product_secondary_id ? " - " + group[0].product_secondary_id : ""
      }`,
      product_available: group[0].product_available,
      product_label: group[0].product_label,
    }));
  }, [endpoint, productsData, proposalSet]);

  useEffect(() => {
    if (endpoint === "variants") {
      if (mappedOptions.length === 0) {
        variantsAvailable(false);
      } else {
        variantsAvailable(true);
      }
    }
  }, [endpoint, mappedOptions, variantsAvailable]);

  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);

  // Find default value in options (adjusted to use local state)
  useEffect(() => {
    if (mappedOptions && defaultValue !== undefined && defaultValue !== null) {
      const defaultOption = mappedOptions.find(
        (option) => String(option.value) === String(defaultValue)
      );

      if (
        JSON.stringify(defaultOption) !== JSON.stringify(selectedOption) ||
        (defaultOption === null && selectedOption !== null) ||
        (defaultOption !== null && selectedOption === null)
      ) {
        setSelectedOption(defaultOption || null);
      }
    }
  }, [defaultValue, mappedOptions]);

  const formValue = useWatch({ control, name });
  useEffect(() => {
    if (formValue && typeof formValue === 'object' && formValue.value) {
      if (!selectedOption || selectedOption.value !== formValue.value) {
        setSelectedOption(formValue);
      }
    }
  }, [formValue, selectedOption]);

  const computedOptions = useMemo(() => {
    const hasProposals = proposalMappedOptions.length > 0;

    if (!hasTyped && endpoint !== "variants") {
      if (hasProposals) {
        const selectedInProposals = selectedOption && proposalSet.has(selectedOption.value);
        return [
          { label: t("suggestions"), options: proposalMappedOptions },
          ...(selectedOption && !selectedInProposals ? [selectedOption] : []),
        ];
      }
      return selectedOption ? [selectedOption] : [];
    }

    if (hasProposals) {
      const filteredOptions = mappedOptions.filter(
        (opt) => !proposalSet.has(opt.value)
      );
      return [
        { label: t("suggestions"), options: proposalMappedOptions },
        { label: t("results"), options: filteredOptions },
      ];
    }

    return mappedOptions;
  }, [hasTyped, selectedOption, mappedOptions, proposalMappedOptions, proposalSet, endpoint, t]);

  const handleSelectChange = (field: any, selectedOption: OptionType | null) => {
    console.log("handleSelectChange" + JSON.stringify(selectedOption, null, 2));
    field.onChange(selectedOption); // Update react-hook-form
    setSelectedOption(selectedOption);

    if (onSelectionChange) {
      onSelectionChange(selectedOption);
    }
  };

  const borderClass = useMemo(() => {
    if (valueConfidence === undefined || isModified || fieldConfig?.isMandatory === false) {
      return "";
    }
    if (valueConfidence < 25) return "border-red-500 border-2";
    if (valueConfidence < 75) return "border-orange-300 border-2";
    return "";
  }, [valueConfidence, isModified, fieldConfig?.isMandatory]);

  if (!productsData && !customersData && defaultValue != null) {
    return <div>{t("loading")}</div>;
  }

  // Fonction pour normaliser les chaînes en supprimant les accents
  const normalizeString = (str: string) => {
    return str
      .normalize("NFD") //
      .replaceAll(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  };

  function customFilterOption(option: any, inputValue: string) {
    if (typeof option.label !== "string") return false;
    const words = inputValue.trim().toLowerCase().split(/\s+/);
    return words.every(
      (word) => {
        const normalizedWord = normalizeString(word);
        return (
          normalizeString(option.label).includes(normalizedWord) ||
          normalizeString(option.data?.customer_email || "").includes(normalizedWord) ||
          normalizeString(option.data?.searchItem || "").includes(normalizedWord)
        );
      }
    );
  }

  const controlStyles = {
    base: `rounded-lg bg-default-100 hover:cursor-pointer text-sm ${borderClass}`,

    focus: [
      "outline-none",
      "ring-0",
      "ring-offset-0",
      "!border-vistagreen",
      "!border-2",
      "!border-solid",
    ].join(" "),
    hover: [
      "outline-none",
      "ring-0",
      "ring-offset-0",
      "!border-vistagreen",
      "!border-2",
      "!border-solid",
      "!bg-default-100",
    ].join(" "),
    nonFocus: "border-gray-300 hover:border-gray-400",
  };
  const placeholderStyles = "text-gray-500 pl-1 py-0.5";
  const selectInputStyles = "pl-1 py-0.5 text-sm";
  const valueContainerStyles = "p-1 gap-1";
  const singleValueStyles = `leading-7 ml-1 ${displayNameInCaps ? 'uppercase' : 'lowercase'}`;
  const multiValueStyles = "bg-gray-100 rounded items-center py-0.5 pl-2 pr-1 gap-1.5";
  const multiValueLabelStyles = "leading-6 py-0.5";
  const multiValueRemoveStyles =
    "border border-gray-200 bg-white hover:bg-red-50 hover:text-red-800 text-gray-500 hover:border-red-300 rounded-md";
  const indicatorsContainerStyles = "p-1 gap-1";
  const clearIndicatorStyles = "text-gray-500 p-1 rounded-md hover:bg-red-50 hover:text-red-800";
  const indicatorSeparatorStyles = "bg-gray-300";
  const dropdownIndicatorStyles = "p-1 hover:bg-gray-100 text-gray-500 rounded-md hover:text-black";
  const menuStyles = "p-1 mt-2 border border-gray-200 bg-white rounded-lg text-sm ";
  const groupHeadingStyles = "ml-3 mt-2 mb-1 text-gray-500";
  const optionStyles = {
    base: `hover:cursor-pointer px-3 py-2 rounded ${displayNameInCaps ? 'uppercase' : 'lowercase'}`,
    focus: "bg-gray-100 active:bg-gray-200",
    selected: "after:content-['✔'] after:ml-2 after:text-green-500 text-gray-500 bg-gray-100",
  };
  const noOptionsMessageStyles =
    "text-gray-500 p-2 bg-gray-50 border border-dashed border-gray-200 rounded-sm";

  const selectStyles: StylesConfig<OptionType, false> = {
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  if (endpoint === "variants" && mappedOptions.length === 0) {
    return <div></div>;
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const filteredOptions = mappedOptions.filter((option) =>
      customFilterOption(option, debouncedSearchTerm)
    );

    if ((event.key === "Tab" || event.key === "Enter") && filteredOptions.length > 1) {
      event.preventDefault();
    }
  };

  const formatOptionLabel = (option: OptionType) => {
    if (endpoint === "products") {
      const productId = option.value;
      const restOfLabel = option.label.replace(productId, '').trim();
      const isPastPurchase = pastPurchasedProductIds?.has(String(productId)) ?? false;
      const isProposal = proposalSet.has(String(productId));

      if (restOfLabel.startsWith('-')) {
        const finalLabel = restOfLabel.substring(1).trim();
        return (
          <>
            {option.product_available === false && (
              <CgUnavailable className="fill-red-500 text-red-500 text-base inline align-text-bottom mb-[1px]" />
            )}
            {isPastPurchase && (
              <HiOutlineBadgeCheck className="text-violet-500 text-base inline align-text-bottom mb-[1px] ml-1" />
            )}
            {isProposal && (
              <BsStars className="text-amber-500 text-base inline align-text-bottom mb-[1px] ml-1" />
            )}
            {productId} - {finalLabel}
            {option.product_label && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white normal-case" style={{ backgroundColor: '#717171' }}>
                {option.product_label}
              </span>
            )}
          </>
        );
      }
    }
    if (endpoint === "customers") {
      const customerId = option.value;
      const restOfLabel = option.label.replace(customerId, '').trim();

      if (restOfLabel.startsWith('-')) {
        const finalLabel = restOfLabel.substring(1).trim();
        return (
          <>
            {customerId}{" "}
            {option.customer_available === false && (
              <Tooltip content={option.customer_availability_description || "Client indisponible"}>
                <span>
                  <CgUnavailable className="fill-red-500 text-red-500 text-base inline align-text-bottom mb-[1px]" />
                </span>
              </Tooltip>
            )}
            - {finalLabel}
          </>
        );
      }
    }
    return option.label;
  };

  if (
    (endpoint === "delivery_address" ||
      endpoint === "billing_address" ||
      (endpoint === "sender_postal_address" && checksOrderMetadata?.sender_address_matching?.onlyInDeliveryPerCustomer === true)) &&
    mappedOptions.length === 0
  ) {
    return <div className="text-gray-500 italic">{t("address_unavailable_in_bdd")}</div>;
  }

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => {
        return (
          <Select
            {...field}
            placeholder={t("search")}
            unstyled
            isDisabled={isDataReadOnlyOperators ?? undefined}
            menuIsOpen={isDataReadOnlyOperators ? false : undefined}
            classNames={{
              control: ({ isFocused, menuIsOpen }) =>
                `${controlStyles.base} ${isFocused || menuIsOpen ? controlStyles.focus : ""}`,
              placeholder: () => placeholderStyles,
              input: () => selectInputStyles,
              valueContainer: () => valueContainerStyles,
              singleValue: () => singleValueStyles,
              multiValue: () => multiValueStyles,
              multiValueLabel: () => multiValueLabelStyles,
              multiValueRemove: () => multiValueRemoveStyles,
              indicatorsContainer: () => indicatorsContainerStyles,
              clearIndicator: () => clearIndicatorStyles,
              indicatorSeparator: () => indicatorSeparatorStyles,
              dropdownIndicator: () => dropdownIndicatorStyles,
              menu: () => menuStyles,
              groupHeading: () => groupHeadingStyles,
              option: ({ isSelected }) =>
                `${optionStyles.base} ${isSelected ? optionStyles.selected : ""}`,
              noOptionsMessage: () => noOptionsMessageStyles,
            }}
            styles={selectStyles}
            options={
              ["delivery_address", "billing_address", "sender_postal_address"].includes(endpoint) &&
              ((endpoint === "delivery_address" &&
                checksOrderMetadata?.delivery_address_matching?.onlyInDeliveryPerCustomer !==
                  false) ||
                (endpoint === "billing_address" &&
                  checksOrderMetadata?.billing_address_matching?.onlyInDeliveryPerCustomer !==
                    false) ||
                (endpoint === "sender_postal_address" &&
                  checksOrderMetadata?.sender_address_matching?.onlyInDeliveryPerCustomer !==
                    false))
                ? mappedOptions
                : computedOptions
            }
            value={selectedOption}
            onChange={(selectedOption) => {
              handleSelectChange(field, selectedOption);
            }}
            onInputChange={onSearchChange}
            filterOption={customFilterOption}
            onKeyDown={handleKeyDown}
            noOptionsMessage={() => noOptionsMessage}
            formatOptionLabel={formatOptionLabel}
            tabIndex={customTabIndex ?? 0}
          />
        );
      }}
    />
  );
};

export default SelectComponent;
